import { useTranslations } from "next-intl";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useRouter } from "next/compat/router";
import {
  useRefinementList,
  useSortBy,
  useInstantSearch,
  usePagination,
  useConfigure,
} from "react-instantsearch";
import { useCurrentUser, useProductsLiked } from "@hooks/use-current-user";
import { useSizes } from "@hooks/use-catalog";
import Offcanvas from "@components/core/Offcanvas";
import findOrderByOptions from "@utils/catalog-filters/findOrderByOptions";
import isServer from "@utils/is-server";
import catalogContexts from "@utils/constants/catalogContexts";
import QuickFiltersList from "./QuickFiltersList";
import CatalogFiltersFooter from "./CatalogFiltersFooter";
import OrderBySection from "./Sections/OrderBySection";
import AvailabilitiesSection from "./Sections/AvailabilitiesSection";
import KindsSection from "./Sections/KindsSection";
import CategorySection from "./Sections/CategorySection";
import SizesSection from "./Sections/SizesSection";
import ColorsSection from "./Sections/ColorsSection";
import SeasonsSection from "./Sections/SeasonsSection";
import LengthSection from "./Sections/LengthSection";
import BrandsSection from "./Sections/BrandsSection";
import PersonalisedSection from "./Sections/PersonalisedSection";

const CatalogFilters = ({
  context,
  listingPage,
  getInitializedUiState,
  shouldReinitializeUiState,
  resetPersonalisedFilters,
  checkedPersonalised,
  filtersConfigure,
  updatePersonalisedFilters,
  userListProducts,
}) => {
  const t = useTranslations("catalog");

  const router = useRouter();
  const user = useCurrentUser();
  const productsLiked = useProductsLiked();

  const isEarlyUser = user?.early_user;

  useRefinementList({ attribute: "catalogs" });
  useRefinementList({ attribute: "subcategory" });
  useRefinementList({ attribute: "collections" });
  const { sizeItems, isFilterAvailableChecked, refineSize } = useSizes();
  const {
    currentRefinement: currentOrderBy,
    options: orderByOptions,
    refine: refineOrderBy,
  } = useSortBy({ items: findOrderByOptions() });
  const { items: categoryItems, refine: refineCategory } = useRefinementList({
    attribute: "category",
    limit: 20,
  });
  const { items: kindsItems } = useRefinementList({ attribute: "segment" });
  const { items: colorItems, refine: refineColor } = useRefinementList({
    attribute: "colors",
    limit: 20,
  });
  const { items: seasonItems, refine: refineSeason } = useRefinementList({
    attribute: "season",
  });
  const { items: sleeveItems, refine: refineSleeve } = useRefinementList({
    attribute: "sleeves",
  });
  const { items: legItems, refine: refineLeg } = useRefinementList({
    attribute: "legs",
  });
  const { items: brandItems, refine: refineBrand } = useRefinementList({
    attribute: "brand.name",
    limit: 200,
    sortBy: ["name:asc"],
  });

  const configureProps = useMemo(() => {
    const hasLargeSizes = () => {
      const appliedSizes = sizeItems
        .filter((sizeItem) => sizeItem.isRefined && sizeItem.value !== "TU")
        .map((sizeItem) => sizeItem.value);

      const numericSizes = appliedSizes
        .map((size) => parseInt(size, 10))
        .filter((size) => !isNaN(size));

      return numericSizes.length > 0 && numericSizes.every((size) => size >= 42);
    };

    const rulesContexts = () => {
      const contexts = [];

      if (isEarlyUser) {
        contexts.push("early_user");
      } else {
        contexts.push("not_early_user");
      }

      if (hasLargeSizes()) {
        contexts.push("large_size");
      } else {
        contexts.push("not_large_size");
      }

      if (isEarlyUser && hasLargeSizes()) {
        contexts.push("early_user_large_size");
      }

      return contexts;
    };

    return {
      getRankingInfo: true,
      userToken: user?.uid,
      filters: filtersConfigure,
      ruleContexts: rulesContexts(),
    };
  }, [filtersConfigure, isEarlyUser, sizeItems, user?.uid]);

  useConfigure(configureProps);

  const { uiState, setUiState } = useInstantSearch();
  const { nbHits: resultsCount } = usePagination();

  const [open, setOpen] = useState(false);
  const [filterSectionOpened, setFilterSectionOpened] = useState("");

  const quickFiltersListRef = useRef(null);

  const onFiltersModalClose = useCallback(() => {
    quickFiltersListRef.current?.scrollTo({ left: 0, behavior: "smooth" });

    setFilterSectionOpened("");
  }, []);

  const clearFiltersAndClose = useCallback(() => {
    const resetedUiState = getInitializedUiState({});
    setUiState(resetedUiState);

    resetPersonalisedFilters();

    setOpen(false);
  }, [getInitializedUiState, resetPersonalisedFilters, setUiState]);

  useEffect(() => {
    function reinitiliazeUiStateOnSamePageNavigation() {
      if (isServer() || !shouldReinitializeUiState(context, router.query, uiState, listingPage)) {
        return;
      }

      const resetedUiState = getInitializedUiState(window.location);
      setUiState(resetedUiState);

      resetPersonalisedFilters();
    }

    reinitiliazeUiStateOnSamePageNavigation();
  }, [router]);

  useEffect(() => {
    function reinitiliazeUiStateUserListProductsChange() {
      if (isServer() || context !== catalogContexts.userlist) return;

      resetPersonalisedFilters();
    }

    reinitiliazeUiStateUserListProductsChange();
  }, [userListProducts]);

  return (
    <div
      className="catalog-filters-sticky-header"
      ref={quickFiltersListRef}
    >
      <QuickFiltersList
        openFilters={() => setOpen(true)}
        setFilterSectionOpened={setFilterSectionOpened}
        isFilterAvailableChecked={isFilterAvailableChecked}
        categoryItems={categoryItems}
        sizeItems={sizeItems}
        colorItems={colorItems}
        seasonItems={seasonItems}
        brandItems={brandItems}
        kindsItems={kindsItems}
        sleeveItems={sleeveItems}
        legItems={legItems}
        checkedPersonalised={checkedPersonalised}
        listingPage={listingPage}
      />

      <Offcanvas
        show={open}
        onHide={() => setOpen(false)}
        title={t("filters.title", { count: 0 })}
        placement="end"
        restoreFocus={false}
        onExited={onFiltersModalClose}
        Footer={
          <CatalogFiltersFooter
            clearFiltersAndClose={clearFiltersAndClose}
            closeFilters={() => setOpen(false)}
            resultsCount={resultsCount}
          />
        }
      >
        <div className="catalog-filters-body">
          <div className="bg-white">
            <OrderBySection
              context={context}
              currentOrderBy={currentOrderBy}
              orderByOptions={orderByOptions}
              refineOrderBy={refineOrderBy}
            />
            <AvailabilitiesSection
              setUiState={setUiState}
              sizeItems={sizeItems}
              isFilterAvailableChecked={isFilterAvailableChecked}
            />
            <KindsSection
              kindsItems={kindsItems}
              uiState={uiState}
              setUiState={setUiState}
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <CategorySection
              categoryItems={categoryItems}
              refineCategory={refineCategory}
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <SizesSection
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
              sizeItems={sizeItems}
              refineSize={refineSize}
            />
            <ColorsSection
              colorItems={colorItems}
              refineColor={refineColor}
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <SeasonsSection
              seasonItems={seasonItems}
              refineSeason={refineSeason}
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <LengthSection
              sleeveItems={sleeveItems}
              legItems={legItems}
              refineSleeve={refineSleeve}
              refineLeg={refineLeg}
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <BrandsSection
              brandItems={brandItems}
              refineBrand={refineBrand}
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
            />
            <PersonalisedSection
              listingPage={listingPage}
              onClose={() => setOpen(false)}
              filterSectionOpened={filterSectionOpened}
              setFilterSectionOpened={setFilterSectionOpened}
              checkedPersonalised={checkedPersonalised}
              updatePersonalisedFilters={updatePersonalisedFilters}
              context={context}
              productsLiked={productsLiked}
              userListProducts={userListProducts}
            />
          </div>
        </div>
      </Offcanvas>
    </div>
  );
};

export default CatalogFilters;
